import { useState, useRef, useEffect } from 'react';
import { BGrainOverlay, BStripes } from './layout';
import './BPinEntry.css';

interface BPinEntryProps {
  onSuccess: () => void;
}

const PIN_LENGTH = 7;

export function BPinEntry({ onSuccess }: BPinEntryProps) {
  const [digits, setDigits] = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.slice(-1);
    if (digit && !/^\d$/.test(digit)) return;

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError(null);

    // Auto-advance to next input
    if (digit && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if complete
    if (digit && index === PIN_LENGTH - 1) {
      const pin = newDigits.join('');
      validatePin(pin);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (pasted.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < pasted.length; i++) {
        newDigits[i] = pasted[i];
      }
      setDigits(newDigits);

      if (pasted.length === PIN_LENGTH) {
        validatePin(pasted);
      } else {
        inputRefs.current[pasted.length]?.focus();
      }
    }
  };

  const validatePin = async (pin: string) => {
    try {
      const response = await fetch('/api/v1/auth/pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        localStorage.setItem('tastymusic_session', 'authenticated');
        setTimeout(() => {
          onSuccess();
        }, 1000);
      } else {
        setIsShaking(true);
        setError(data.error?.message || "That's not the number.");
        setShowHint(true);
        setTimeout(() => {
          setIsShaking(false);
          setDigits(Array(PIN_LENGTH).fill(''));
          inputRefs.current[0]?.focus();
        }, 600);
      }
    } catch {
      setIsShaking(true);
      setError('Connection error');
      setTimeout(() => {
        setIsShaking(false);
        setDigits(Array(PIN_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      }, 600);
    }
  };

  return (
    <div className="b-pin-page">
      <BStripes />
      <BGrainOverlay />

      <div className={`b-pin-container ${isShaking ? 'b-pin-container--shake' : ''} ${isSuccess ? 'b-pin-container--success' : ''}`}>
        {/* Logo */}
        <div className="b-pin-logo">
          <img src="/tasty-logo.png" alt="TastyMusic" className="b-pin-logo__img" />
        </div>

        {/* Brand */}
        <h1 className="b-pin-title">TastyMusic</h1>
        <span className="b-pin-tagline">AI-Powered Music Discovery</span>

        {/* Prompt */}
        <p className="b-pin-prompt">
          {isSuccess ? "You're in." : error || "Got the number?"}
        </p>

        {/* PIN Inputs */}
        <div className="b-pin-inputs" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`b-pin-digit ${digit ? 'b-pin-digit--filled' : ''} ${isSuccess ? 'b-pin-digit--success' : ''}`}
              disabled={isSuccess}
              autoComplete="off"
            />
          ))}
        </div>

        {/* Hint */}
        {showHint && !isSuccess && (
          <p className="b-pin-hint">hint: jenny's number</p>
        )}
      </div>
    </div>
  );
}
